// BlackMirror theme bridge v2 (theme asset assets/bm-vi-bridge.js, replacing v1 storefront-pixel/theme-bridge.js).
// Everything v1 does, unchanged: the buying guide, the setup finder and the product-page enquiry POST to
// /api/public/storefront/guide or /enquiry and get back { ok: true, request_id }; this script publishes one custom
// event, storefront_request_submitted, with the request id, for the pixel to forward.
// v2 adds, for BlackMirror Journey:
// - contact_link_clicked { link_kind: 'tel' | 'wa' } when a tel: or WhatsApp link is tapped;
// - demo_booking_submitted { booking_uid } when an embedded Cal.com booking (cal.futuremvmt.com) succeeds;
// - cart attributes that reach the order as note_attributes and survive an ad blocker: _bm_cid (the Shopify
//   client id, the _shopify_y cookie), _bm_lt (the first landing of this tab session, bounded, format below),
//   _bm_fbp and _bm_fbc. Written only to a cart that already holds an item, so no cart is ever created for a
//   visitor.
// Tracking v2 (2 Nov 2026): no consent gate. Every event carries fp, the browser fingerprint computed on this page
// (fingerprint.js, inlined by render.js at its fingerprint token line) once per page, when the page is first idle,
// and kept in memory only; the pixel stores it with the event's client id. An event waits for it at most 400 ms and
// is published without fp after that. Events and cart writes need only the live theme. It never changes a
// request, a response or what the page's own code receives. The theme serves render.js's bm-vi-bridge.js, never
// this file as it stands.
//
// _bm_lt is a query string, at most 1000 characters: v=1, lp (landing path, at most 200), rf (referrer host,
// only when it is another site), ts (landing time, unix seconds), then each attribution key below that the landing
// URL carried, first value only, at most 100 characters each, in that order; a key that would overflow is skipped.
(function () {
  'use strict';
  var w = window;
  if (w.__bmViBridge || typeof w.fetch !== 'function') return;

  var EVENT = 'storefront_request_submitted';
  var FORM_PATH = /\/api\/public\/storefront\/(?:guide|enquiry)$/;
  var CART_ADD_PATH = /\/cart\/add(?:\.js)?$/;
  var UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  var BOOKING_UID = /^[A-Za-z0-9_-]{1,64}$/;
  var FB_COOKIE = /^fb\.\d{1,2}\.\d{1,16}\.[A-Za-z0-9_.-]{1,480}$/;
  var CLIENT_ID = /^[A-Za-z0-9_.:-]{1,80}$/;
  var FP = /^[0-9a-f]{16,64}$/;
  // The longest a bridge event waits for the fingerprint before it is published without fp.
  var FP_WAIT_MS = 400;
  var CAL_ORIGIN = 'https://cal.futuremvmt.com';
  var WA_HOSTS = { 'wa.me': true, 'api.whatsapp.com': true };
  var LT_STORE = 'bm_lt';
  var LT_MAX = 1000;
  // Ad click ids and per-visitor ids. Same list as storefront-pixel/click-keys.js.
  var CLICK_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid', 'srsltid', 'tmid', 'tmsid'];
  // Click ids and utm_campaign first, so they are the last to be cut by the length bound.
  var LT_KEYS = CLICK_KEYS.concat([
    'utm_campaign', 'utm_source', 'utm_medium', 'utm_content', 'utm_term', 'tmsrc', 'tmcid',
    'gad_campaignid', 'gad_source', 'campaign_id', 'ad_id'
  ]);
  var original = w.fetch;
  var published = new Set();
  var bookings = new Set();

  function noop() {}

  // ---- theme and fingerprint ---------------------------------------------------

  function shopify() { return w.Shopify || null; }
  // Read at the moment of use, because the theme role can change after the page loads.
  function liveTheme() {
    var s = shopify();
    return !!(s && s.theme && s.theme.role === 'main' && !s.designMode);
  }

  // BlackMirror browser fingerprint v2 (storefront-pixel/fingerprint.js). One function, written once: render.js
  // inlines this file into the custom pixel (pixel.template.js) and the theme bridge (bridge-v2.js) in place of
  // their fingerprint token line. It is never served on its own and is not a Node module.
  //
  // bmFingerprint(g) reads the browser through g (the window, or self inside the pixel sandbox) and resolves to a
  // hex string the ingest accepts (^[0-9a-f]{16,64}$):
  //   'f2' + the first 62 hex of SHA-256 (crypto.subtle), or
  //   'e2' + 16 hex of 64-bit FNV-1a, where crypto.subtle is missing or fails.
  // The inputs, in this order: WebGL UNMASKED_VENDOR and UNMASKED_RENDERER, a 20-font availability probe by text
  // width, screen width, height and colorDepth, devicePixelRatio, the Intl time zone,
  // navigator.language and languages, hardwareConcurrency, deviceMemory, platform and maxTouchPoints. Each is read
  // in its own try/catch and becomes '-' when it cannot be read, so a browser that blocks canvas or WebGL still gets
  // a stable value. v2 drops v1's canvas 2D render: Safari's Advanced Fingerprinting Protection and Brave add canvas
  // noise per origin and per session, so the pixel sandbox, the main page and an in-app webview never agreed on it. It never throws and never rejects. Nothing is stored, written or sent from here; the caller
  // keeps the result in memory for the page.
  function bmFingerprint(g) {
    var NA = '-';
    var FONTS = ['Arial', 'Arial Black', 'Calibri', 'Cambria', 'Comic Sans MS', 'Consolas', 'Courier New', 'Georgia',
      'Helvetica', 'Helvetica Neue', 'Impact', 'Lucida Console', 'Menlo', 'Monaco', 'Palatino', 'Roboto', 'Segoe UI',
      'Tahoma', 'Times New Roman', 'Verdana'];
    var PROBE = 'mmmmmmmmmmlli10OOWw';
    var BASES = ['monospace', 'sans-serif', 'serif'];

    function read(fn) {
      try {
        var value = fn();
        return value === undefined || value === null || value === '' ? NA : String(value);
      } catch (e) {
        return NA;
      }
    }
    function canvas() {
      return g.document.createElement('canvas');
    }
    // [vendor, renderer] from one WebGL context, which is released afterwards.
    function webgl() {
      var c = canvas();
      var gl = c.getContext('webgl') || c.getContext('experimental-webgl');
      var info = gl.getExtension('WEBGL_debug_renderer_info');
      var out = [read(function () { return gl.getParameter(info.UNMASKED_VENDOR_WEBGL); }),
        read(function () { return gl.getParameter(info.UNMASKED_RENDERER_WEBGL); })];
      try {
        var lose = gl.getExtension('WEBGL_lose_context');
        if (lose) lose.loseContext();
      } catch (e) { /* the context is dropped with the canvas */ }
      return out;
    }
    // One bit per font: 1 when the text width differs from a base family's.
    function fonts() {
      var ctx = canvas().getContext('2d');
      var width = function (family) {
        ctx.font = '72px ' + family;
        return ctx.measureText(PROBE).width;
      };
      var base = [];
      var bits = '';
      for (var b = 0; b < BASES.length; b += 1) base.push(width(BASES[b]));
      for (var i = 0; i < FONTS.length; i += 1) {
        var found = false;
        for (var j = 0; j < BASES.length && !found; j += 1) {
          found = width('"' + FONTS[i] + '", ' + BASES[j]) !== base[j];
        }
        bits += found ? '1' : '0';
      }
      return bits;
    }
    function utf8(text) {
      var out = [];
      for (var i = 0; i < text.length; i += 1) {
        var c = text.charCodeAt(i);
        if (c >= 0xd800 && c <= 0xdbff && i + 1 < text.length) {
          var d = text.charCodeAt(i + 1);
          if (d >= 0xdc00 && d <= 0xdfff) {
            c = 0x10000 + ((c - 0xd800) << 10) + (d - 0xdc00);
            i += 1;
          }
        }
        if (c < 0x80) out.push(c);
        else if (c < 0x800) out.push(0xc0 | (c >> 6), 0x80 | (c & 63));
        else if (c < 0x10000) out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
        else out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 63), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
      }
      return out;
    }
    function hex(value, digits) {
      var text = value.toString(16);
      while (text.length < digits) text = '0' + text;
      return text;
    }
    // 64-bit FNV-1a in four 16-bit limbs (h0 lowest), so it needs no BigInt.
    function fnv(bytes) {
      var h0 = 0x2325;
      var h1 = 0x8422;
      var h2 = 0x9ce4;
      var h3 = 0xcbf2;
      for (var i = 0; i < bytes.length; i += 1) {
        h0 ^= bytes[i];
        // h * 0x100000001b3 = h * 0x1b3 + (h << 40)
        var t0 = h0 * 0x1b3;
        var t1 = h1 * 0x1b3;
        var t2 = h2 * 0x1b3 + (h0 << 8);
        var t3 = h3 * 0x1b3 + (h1 << 8);
        t1 += t0 >>> 16;
        h0 = t0 & 0xffff;
        t2 += t1 >>> 16;
        h1 = t1 & 0xffff;
        t3 += t2 >>> 16;
        h2 = t2 & 0xffff;
        h3 = t3 & 0xffff;
      }
      return 'e2' + hex(h3, 4) + hex(h2, 4) + hex(h1, 4) + hex(h0, 4);
    }

    var gl = [NA, NA];
    try { gl = webgl(); } catch (e) { /* no WebGL */ }
    var parts = [
      'bmfp=2',
      'gl_vendor=' + gl[0],
      'gl_renderer=' + gl[1],
      'fonts=' + read(fonts),
      'screen_w=' + read(function () { return g.screen.width; }),
      'screen_h=' + read(function () { return g.screen.height; }),
      'color_depth=' + read(function () { return g.screen.colorDepth; }),
      'dpr=' + read(function () { return g.devicePixelRatio; }),
      'tz=' + read(function () { return g.Intl.DateTimeFormat().resolvedOptions().timeZone; }),
      'lang=' + read(function () { return g.navigator.language; }),
      'langs=' + read(function () { return Array.prototype.join.call(g.navigator.languages, ','); }),
      'cores=' + read(function () { return g.navigator.hardwareConcurrency; }),
      'memory=' + read(function () { return g.navigator.deviceMemory; }),
      'platform=' + read(function () { return g.navigator.platform; }),
      'touch=' + read(function () { return g.navigator.maxTouchPoints; })
    ];
    var bytes = utf8(parts.join('\n'));
    try {
      var subtle = g.crypto.subtle;
      if (subtle && typeof subtle.digest === 'function') {
        return Promise.resolve(subtle.digest('SHA-256', new Uint8Array(bytes))).then(function (buffer) {
          var view = new Uint8Array(buffer);
          var out = 'f2';
          for (var i = 0; i < view.length; i += 1) out += hex(view[i], 2);
          return out.slice(0, 64);
        }).then(function (value) {
          return /^f2[0-9a-f]{62}$/.test(value) ? value : fnv(bytes);
        }, function () {
          return fnv(bytes);
        });
      }
    } catch (e) { /* no crypto.subtle */ }
    return Promise.resolve(fnv(bytes));
  }

  var fpOnce = null;
  // Computed once per page (on idle at load, or by the first event if that comes sooner), kept in memory only;
  // null when it fails or was not inlined.
  function fingerprint() {
    if (fpOnce === null) {
      try {
        fpOnce = typeof bmFingerprint === 'function'
          ? Promise.resolve(bmFingerprint(w)).then(function (value) {
            return typeof value === 'string' && FP.test(value) ? value : null;
          }, function () { return null; })
          : Promise.resolve(null);
      } catch (e) {
        fpOnce = Promise.resolve(null);
      }
    }
    return fpOnce;
  }

  // The fingerprint, or null when it fails, was not inlined or is not there within FP_WAIT_MS. Without a timer it
  // waits for the fingerprint.
  function fingerprintWithin() {
    return new Promise(function (resolve) {
      var timer = null;
      try { timer = w.setTimeout(function () { resolve(null); }, FP_WAIT_MS); } catch (e) { /* no timer */ }
      fingerprint().then(function (fp) {
        try { if (timer !== null && typeof w.clearTimeout === 'function') w.clearTimeout(timer); } catch (e) { /* it resolves nothing */ }
        resolve(fp);
      }, function () { resolve(null); });
    });
  }

  // Publishes once the fingerprint is there or FP_WAIT_MS has passed (a microtask when it already is), with fp
  // added when it is known. True when the publish was issued: the analytics API is there and this is the live
  // theme.
  function publishEvent(name, data) {
    var s = shopify();
    var analytics = s && s.analytics;
    if (!analytics || typeof analytics.publish !== 'function') return false;
    if (!liveTheme()) return false;
    fingerprintWithin().then(function (fp) {
      try {
        if (fp) data.fp = fp;
        analytics.publish(name, data);
      } catch (e) { /* never break the storefront */ }
    }, noop);
    return true;
  }

  // ---- v1: storefront forms ------------------------------------------------------

  function methodAndUrl(input, init) {
    var url = null;
    var method = null;
    if (typeof input === 'string') {
      url = input;
    } else if (input && typeof input === 'object') {
      if (typeof input.url === 'string') url = input.url;
      else if (typeof input.href === 'string') url = input.href;
      if (typeof input.method === 'string') method = input.method;
    }
    if (init && typeof init.method === 'string') method = init.method;
    if (url === null || method === null || method.toUpperCase() !== 'POST') return null;
    return new w.URL(url, w.location && w.location.href).pathname;
  }

  // An id counts as published only after a publish was issued, so an answer seen on a page without the
  // analytics API can still publish later.
  function publish(id) {
    if (published.has(id)) return;
    if (publishEvent(EVENT, { request_id: id })) published.add(id);
  }

  function onBody(body) {
    try {
      if (!body || typeof body !== 'object' || body.ok !== true) return;
      var id = body.request_id;
      if (typeof id !== 'string' || !UUID.test(id)) return;
      publish(id.toLowerCase());
    } catch (e) { /* never break the storefront */ }
  }

  function isJson(type) {
    if (typeof type !== 'string') return false;
    var media = type.split(';')[0].trim().toLowerCase();
    return media === 'application/json' || media.slice(-5) === '+json';
  }

  // Only a clone is read, so the page's own code still gets an unread body.
  function onFormResponse(res) {
    try {
      if (!res || res.ok !== true) return;
      var headers = res.headers;
      if (!headers || typeof headers.get !== 'function' || !isJson(headers.get('content-type'))) return;
      var parsed = res.clone().json();
      if (parsed && typeof parsed.then === 'function') parsed.then(onBody, noop);
    } catch (e) { /* never break the storefront */ }
  }

  function onCartAddResponse(res) {
    try {
      if (res && res.ok === true) syncCart();
    } catch (e) { /* never break the storefront */ }
  }

  function bridgedFetch() {
    var watch = null;
    try {
      var path = methodAndUrl(arguments[0], arguments[1]);
      if (path !== null && FORM_PATH.test(path)) watch = onFormResponse;
      else if (path !== null && CART_ADD_PATH.test(path)) watch = onCartAddResponse;
    } catch (e) { /* not a call we can read */ }
    // No try/catch here: a synchronous throw of the original reaches the caller unchanged.
    var promise = original.apply(w, arguments);
    if (watch) {
      try {
        // A branch off the original promise with both handlers, so it can never reject unhandled (see v1).
        if (promise && typeof promise.then === 'function') promise.then(watch, noop);
      } catch (e) { /* never break the storefront */ }
    }
    return promise;
  }

  // ---- v2: taps ------------------------------------------------------------------

  function linkKind(href) {
    if (typeof href !== 'string') return null;
    var text = href.trim();
    if (/^tel:/i.test(text)) return 'tel';
    if (/^whatsapp:/i.test(text)) return 'wa';
    if (!/^(https?:)?\/\//i.test(text)) return null;
    var host = new w.URL(text, w.location.href).hostname.toLowerCase();
    return WA_HOSTS[host] === true ? 'wa' : null;
  }

  function onClick(event) {
    try {
      var target = event && event.target;
      var link = target && typeof target.closest === 'function' ? target.closest('a[href]') : null;
      if (!link) return;
      var kind = linkKind(link.getAttribute('href'));
      if (kind) publishEvent('contact_link_clicked', { link_kind: kind });
    } catch (e) { /* never break the storefront */ }
  }

  // ---- v2: Cal.com embed ---------------------------------------------------------

  // The embed iframe posts { originator: 'CAL', type, data } to this window once a booking is saved.
  function onMessage(event) {
    try {
      if (!event || event.origin !== CAL_ORIGIN) return;
      var message = event.data;
      if (!message || typeof message !== 'object' || message.originator !== 'CAL' || message.type !== 'bookingSuccessfulV2') return;
      var uid = message.data && message.data.uid;
      if (typeof uid === 'string' && BOOKING_UID.test(uid)) {
        if (bookings.has(uid)) return;
        if (publishEvent('demo_booking_submitted', { booking_uid: uid })) bookings.add(uid);
        return;
      }
      publishEvent('demo_booking_submitted', {});
    } catch (e) { /* never break the storefront */ }
  }

  // ---- v2: cart attributes -------------------------------------------------------

  function cookie(name) {
    try {
      var parts = String(w.document.cookie || '').split(';');
      for (var i = 0; i < parts.length; i += 1) {
        var pair = parts[i].trim();
        if (pair.indexOf(name + '=') === 0) return decodeURIComponent(pair.slice(name.length + 1));
      }
    } catch (e) { /* unreadable cookie */ }
    return null;
  }
  function shaped(value, re) { return typeof value === 'string' && re.test(value) ? value : null; }

  function clip(value, max) { return String(value).slice(0, max); }

  function buildLanding() {
    var loc = w.location;
    var params = new w.URLSearchParams(loc.search || '');
    var out = new w.URLSearchParams();
    out.append('v', '1');
    out.append('lp', clip(loc.pathname || '/', 200));
    var referrer = w.document.referrer;
    if (referrer) {
      var host = new w.URL(referrer).hostname.toLowerCase();
      if (host && host !== String(loc.hostname || '').toLowerCase()) out.append('rf', clip(host, 100));
    }
    out.append('ts', String(Math.floor(Date.now() / 1000)));
    for (var i = 0; i < LT_KEYS.length; i += 1) {
      var value = params.get(LT_KEYS[i]);
      if (value === null || value === '') continue;
      out.append(LT_KEYS[i], clip(value, 100));
      if (out.toString().length > LT_MAX) out.delete(LT_KEYS[i]);
    }
    return out.toString();
  }

  // The first landing this tab saw, kept for the tab session.
  function landing() {
    var saved = null;
    try { saved = w.sessionStorage.getItem(LT_STORE); } catch (e) { saved = null; }
    var value = typeof saved === 'string' && saved !== '' && saved.length <= LT_MAX ? saved : null;
    if (value !== null) return value;
    try { value = buildLanding(); } catch (e) { return null; }
    try { w.sessionStorage.setItem(LT_STORE, value); } catch (e) { /* storage blocked: this page only */ }
    return value;
  }

  // What the cart's bm attributes should change to, or null for nothing. Keys are only ever set, never cleared.
  function patchFor(cart) {
    var have = (cart && cart.attributes && typeof cart.attributes === 'object') ? cart.attributes : {};
    var want = {};
    // Read on every page, so the tab's first landing is kept even before the cart has an item.
    var lt = landing();
    if (!!cart && typeof cart.item_count === 'number' && cart.item_count > 0) {
      var cid = shaped(cookie('_shopify_y'), CLIENT_ID);
      if (cid) want._bm_cid = cid;
      // The first landing written to the cart is kept: never overwritten.
      if (!have._bm_lt && lt) want._bm_lt = lt;
      var fbp = shaped(cookie('_fbp'), FB_COOKIE);
      var fbc = shaped(cookie('_fbc'), FB_COOKIE);
      if (fbp) want._bm_fbp = fbp;
      if (fbc) want._bm_fbc = fbc;
    }
    var patch = null;
    for (var key in want) {
      if (!Object.prototype.hasOwnProperty.call(want, key)) continue;
      var current = typeof have[key] === 'string' ? have[key] : '';
      if (current === want[key]) continue;
      patch = patch || {};
      patch[key] = want[key];
    }
    return patch;
  }

  function cartRoot() {
    var routes = shopify() && shopify().routes;
    var root = routes && typeof routes.root === 'string' ? routes.root : '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  var syncing = false;
  var again = false;
  // One sync at a time; a request made meanwhile runs once more afterwards.
  function syncCart() {
    if (syncing) { again = true; return; }
    if (!liveTheme()) return;
    syncing = true;
    var done = function () {
      syncing = false;
      if (again) { again = false; syncCart(); }
    };
    try {
      var root = cartRoot();
      original.call(w, root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (res) { return res && res.ok === true ? res.json() : null; })
        .then(function (cart) {
          var patch = cart ? patchFor(cart) : null;
          if (!patch) return null;
          return original.call(w, root + 'cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: patch })
          });
        })
        .then(done, done);
    } catch (e) {
      done();
    }
  }

  // ---- start -----------------------------------------------------------------------

  try {
    w.__bmViBridge = true;
    w.fetch = bridgedFetch;
  } catch (e) { /* fetch is locked down: leave it alone */ }
  // Each handler checks the theme role itself, at the moment it runs.
  try {
    w.document.addEventListener('click', onClick, true);
    w.addEventListener('message', onMessage, false);
    syncCart();
  } catch (e) { /* never break the storefront */ }
  // The fingerprint is computed once the page is idle (after 1 ms without requestIdleCallback), on the live theme
  // only, so an event rarely waits for it.
  try {
    var warm = function () {
      try { if (liveTheme()) fingerprint(); } catch (e) { /* the first event computes it instead */ }
    };
    if (typeof w.requestIdleCallback === 'function') w.requestIdleCallback(warm);
    else w.setTimeout(warm, 1);
  } catch (e) { /* the first event computes it instead */ }
})();
